import React from "react";
import ReactSignatureCanvas from "react-signature-canvas";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { SIGNATURE_ICON } from "../../utils/svgs_new";
import { AppButton } from "../Generic/AppButton";
import { AppIcon } from "../Generic/AppIcon";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import { AppOverlay } from "../Generic/AppOverlay";
import Flex from "../Generic/Flex";
import { isDefined } from "../Generic/IsDefined";

export interface SignaturePayload {
  trimmed: string;
  original: string;
}

interface SignatureModalProps {
  isOpen: boolean;
  initialData: string | null;
  onSubmit: (payload: SignaturePayload | null) => void;
  onRequestClose: () => void;
}

const SignatureModalV2 = (props: SignatureModalProps) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<ReactSignatureCanvas>(null);
  const [canvasProps, setCanvasProps] = React.useState({ width: 0, height: 0 });
  const [draft, setDraft] = React.useState<SignaturePayload | null>(null);
  const [displayPlaceholder, setDisplayPlaceholder] = React.useState(!isDefined(props.initialData));

  const updateCanvasProps = React.useCallback(() => {
    const { current: modalDom } = modalRef;

    if (modalDom !== null) {
      setCanvasProps({
        width: modalDom.clientWidth,
        height: modalDom.clientHeight,
      });
    }

    if (draft === null && props.isOpen && isDefined(props.initialData)) {
      canvasRef.current?.fromDataURL(props.initialData, {
        width: modalRef.current?.clientWidth ?? 0,
        height: modalRef.current?.clientHeight ?? 0,
        ratio: 1,
      });
    }
  }, [draft, props.initialData, props.isOpen]);

  const handleBeginSignature = () => setDisplayPlaceholder(false);

  const handleEndSignature = () => {
    if (canvasRef.current !== null) {
      setDraft({
        original: canvasRef.current.getCanvas().toDataURL(),
        trimmed: canvasRef.current.getTrimmedCanvas().toDataURL(),
      });
    }
  };

  const handleClickClear = () => {
    canvasRef.current?.clear();
    setDisplayPlaceholder(draft === null);
    setDraft(null);
  };

  const handleClickCancel = () => {
    props.onRequestClose();
    setDraft(null);
  };

  const handleClickSubmit = () => {
    props.onSubmit(draft);
    setDraft(null);
  };

  React.useEffect(() => {
    updateCanvasProps();

    window.addEventListener("resize", updateCanvasProps);

    return () => window.removeEventListener("resize", updateCanvasProps);
  }, [updateCanvasProps]);

  React.useEffect(() => {
    if (props.isOpen) {
      updateCanvasProps();
    }
  }, [props.isOpen, updateCanvasProps]);

  return (
    <AppOverlay>
      <AppAbstractModal
        isOpen={props.isOpen}
        onRequestClose={props.onRequestClose}
        className="SignatureModalV2"
      >
        <div className="signature-wrapper" ref={modalRef}>
          {displayPlaceholder ? (
            <AppIcon icon={SIGNATURE_ICON} className="signature-placeholder" />
          ) : null}

          <ReactSignatureCanvas
            canvasProps={canvasProps}
            ref={canvasRef}
            onBegin={handleBeginSignature}
            onEnd={handleEndSignature}
          />
        </div>

        <Flex xGap={4} className="actions">
          <AppButton size="small" onClick={handleClickCancel} variant="soft" color="danger">
            {translate(STRINGS.CANCEL)}
          </AppButton>

          <AppButton size="small" onClick={handleClickClear} variant="soft" color="black">
            {isDefined(props.initialData) && isDefined(draft)
              ? translate(STRINGS.UNDO)
              : translate(STRINGS.CLEAR)}
          </AppButton>

          <AppButton size="small" onClick={handleClickSubmit} variant="soft">
            {translate(STRINGS.SUBMIT)}
          </AppButton>
        </Flex>
      </AppAbstractModal>
    </AppOverlay>
  );
};

export default SignatureModalV2;
